import { useIntl } from "react-intl";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import Button from "@rio-cloud/rio-uikit/Button";
import Select from "@rio-cloud/rio-uikit/Select";
import DatePicker from "@rio-cloud/rio-uikit/DatePicker";
import TimePicker from "@rio-cloud/rio-uikit/TimePicker";
import { useEffect, useState } from "react";
import {
  ServiceBay,
  ServiceBaySchedule,
} from "../../../../../../../models/serviceBay";
import ErrorDialog from "../../../../../../../components/dialogs/ErrorDialog";
import { Occurrence } from "../../../../../../../models/occurrence";
import ConfirmDialog from "../../../../../../../components/dialogs/ConfirmDialog";
import VehicleIcon from "../../../../../../../components/VehicleIcon";
import { fetchSchedulingByChassisList } from "../../../../../../../api/fetchScheduling";
import { checkServiceBayAvailability } from "../../../../../../../utils/serviceBayAvailabilityUtils";

interface IServiceBayScheduleForm {
  serviceBay?: string | null;
  startDate?: Date;
  startTime?: Date;
  endDate?: Date;
  endTime?: Date;
  occurrence?: string | null;
}

interface IServiceBayScheduleModalProps {
  scheduleServiceBays: ServiceBaySchedule[];
  serviceBays: ServiceBay[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  onDelete: (data: any) => Promise<void>;
  initialData?: ServiceBaySchedule;

  occurrences: Occurrence[];
  occurrencePage: number;
  occurrenceLoading: boolean;
  occurrenceHasMoreData: boolean;
  getOccurrences: (page: number) => void;
}

const ServiceBayScheduleDialog = ({
  scheduleServiceBays,
  serviceBays,
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialData,

  occurrences,
  occurrencePage,
  occurrenceLoading,
  occurrenceHasMoreData,
  getOccurrences,
}: IServiceBayScheduleModalProps) => {
  const intl = useIntl();
  const [occurrenceOptions, setOccurrenceOptions] = useState<boolean>(false);

  const [isView, setIsView] = useState(false);

  const [isServiceBayAvailable, setIsServiceBayAvailable] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<IServiceBayScheduleForm>({});
  const [scheduleNumber, setScheduleNumber] = useState<string | null>(null);

  // Para evitar erro de renderização do TimePicker
  const [endTimePickerValue, setEndTimePickerValue] = useState<string>("");

  const getNextHour = () => {
    const now = new Date();
    now.setMinutes(0);
    now.setHours(now.getHours() + 1);
    return now;
  }

  const getScheduleDateAndNumber = async (): Promise<{ date?: Date, number?: string }> => {
    if (!initialData?.occurrence?.vehicleChassis || !initialData?.occurrence?.scheduleNumber) return { date: undefined, number: undefined };
    const schedulings = await fetchSchedulingByChassisList([initialData?.occurrence?.vehicleChassis!]);
    const scheduling = schedulings.find((scheduling) => scheduling?.scheduleNumber === initialData?.occurrence?.scheduleNumber);
    return { date: scheduling?.date, number: scheduling?.scheduleNumber };
  }

  useEffect(() => {
    const fillForm = async () => {
      setOccurrenceOptions(false);
      const scheduleDateAndNumber = await getScheduleDateAndNumber();
      if(initialData) {
        setForm({
          serviceBay: initialData.serviceBay?.id,
          startDate: initialData.startDate,
          startTime: initialData.startDate,
          endDate: initialData.endDate,
          endTime: initialData.endDate,
          occurrence: initialData?.occurrence?.id,
        });
      } else {
        setForm({
          serviceBay: undefined,
          startDate: scheduleDateAndNumber?.date || getNextHour(),
          startTime: scheduleDateAndNumber?.date || getNextHour(),
          endDate: undefined,
          endTime: undefined,
          occurrence: undefined,
        });
      }
      setScheduleNumber(scheduleDateAndNumber?.number || null);
    }
    
    setIsView(!!initialData);
    fillForm();
  }, [isOpen]);

  const handleChange = (key: string, value: string | Date | undefined) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    checkServiceBayAvailabilityFromForm({ ...form, [key]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (!form.serviceBay || !form.startDate || !form.startTime || !form.endDate || !form.endTime) {
      setError(intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.requiredFields" }));
      setIsLoading(false);
      return;
    }

    try {
        await onSave({
          serviceBay: form.serviceBay,
          occurrence: form.occurrence,
          startDate: form.startDate,
          endDate: form.endDate,
        });
        onClose();
    } catch (error: any) {
      setError(error.message || intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.createError" }));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verifica disponibilidade baseada no formulário (mantém compatibilidade)
   */
  const checkServiceBayAvailabilityFromForm = (formToCheck: IServiceBayScheduleForm) => {
    if (!formToCheck.serviceBay || !formToCheck.startDate || !formToCheck.startTime || !formToCheck.endDate || !formToCheck.endTime) {
      setIsServiceBayAvailable(false);
      return;
    }

    // Converte as datas do formulário para Date objects
    let startDate: Date;
    let endDate: Date;

    try {
      // Processa a data de início
      startDate = formToCheck.startDate;

      // Processa a data de fim
      endDate = formToCheck.endDate;

      // Adiciona os horários às datas
      const [startHour, startMinute] = (formToCheck.startTime?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) || "").split(":").map(Number);
      const [endHour, endMinute] = (formToCheck.endTime?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) || "").split(":").map(Number);

      startDate.setHours(startHour, startMinute, 0, 0);
      endDate.setHours(endHour, endMinute, 0, 0);

      // Verifica disponibilidade
      const isAvailable = checkServiceBayAvailability(scheduleServiceBays, formToCheck.serviceBay, startDate, endDate);
      setIsServiceBayAvailable(isAvailable);
    } catch (error) {
      console.error("Erro ao processar datas:", error);
      setIsServiceBayAvailable(false);
    }
  };

  const updateStartDate = async (chassis: string) => {
    const scheduleDateAndNumber = await getScheduleDateAndNumber();
    if (scheduleDateAndNumber.date && scheduleDateAndNumber.number) {
      setForm((prev) => ({
        ...prev,
        startDate: scheduleDateAndNumber.date,
        startTime: scheduleDateAndNumber.date,
      }));
      setScheduleNumber(scheduleDateAndNumber.number || null);
    }
  }

  return (
    <>
      <Dialog show={isOpen} onClose={onClose} bsSize="md">
        <form onSubmit={handleSubmit}>
          <Dialog.Title title={isView ? intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.titleView" }) : intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.titleCreate" })} />

          <Dialog.Body>
            <div>
              {form.occurrence || isView ? (
                <div className={`w-full border border-[#DADCE0] dark:border-neutral-700 rounded p-8 flex items-center justify-between ${initialData?.id ? "cursor-default" : "cursor-pointer"}`}>
                  <div
                    onClick={() => initialData?.id ? null : setOccurrenceOptions(!occurrenceOptions)}
                    className="flex items-center gap-6 text-[16px] w-full"
                  >
                    <VehicleIcon 
                      chassis={initialData?.occurrence?.vehicleChassis || occurrences.find((occurrence) => occurrence.id === form.occurrence)?.vehicleChassis} 
                      size={64}
                    />
                    <div className="flex flex-col w-full">
                      <span className="font-semibold text-size-14">
                        {
                          initialData?.occurrence?.vehicleModel ||
                          initialData?.occurrence?.vehicleName ||
                          occurrences.find((occurrence) => occurrence.id === form.occurrence)?.vehicleModel ||
                          occurrences.find((occurrence) => occurrence.id === form.occurrence)?.vehicleName ||
                          intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.noName" })
                        }
                      </span>
                      <span className="flex justify-between w-full text-size-14">
                        <span>
                          {intl.formatMessage({ id: "intl-msg:calendar.card.chassis" })}{" "}
                          {initialData?.occurrence?.vehicleChassis ?? occurrences.find((occurrence) => occurrence.id === form.occurrence)?.vehicleChassis ?? "-"}
                        </span>
                        <div className="flex gap-8">
                          <span className="mt-[-8px]">
                            <strong>
                              {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.occurrenceLabel" })}{" "}
                              {occurrences.find(
                                (occurrence) =>
                                  occurrence.id === form.occurrence
                              )?.scheduleNumber || scheduleNumber || "-"}
                            </strong>
                          </span>
                          <span
                            className={`mt-[-4px] rioglyph rioglyph-chevron-down ${occurrenceOptions
                              ? "rioglyph-chevron-up"
                              : "rioglyph-chevron-down"
                              }`}
                          />
                        </div>
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="w-full border border-[#DADCE0] dark:border-neutral-700 rounded p-8 flex items-center justify-between cursor-pointer"
                  onClick={() => setOccurrenceOptions(!occurrenceOptions)}
                >
                  <div className="flex items-center gap-3 text-[16px]">
                    <span className="rioglyph rioglyph-truck text-[20px]" />
                    {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.selectOccurrence" })}
                  </div>
                  <span
                    className={`rioglyph rioglyph-chevron-down ${occurrenceOptions
                      ? "rioglyph-chevron-up"
                      : "rioglyph-chevron-down"
                      }`}
                  />
                </div>
              )}

              {occurrenceOptions && (
                <div className="bg-white dark:bg-neutral-800 dark:text-neutral-100 rounded shadow p-2 mt-2 z-10 w-full max-h-80 overflow-y-auto">
                  {occurrences
                    .filter((occurrence) => !scheduleServiceBays.find(schedule => schedule.occurrence?.id === occurrence.id))
                    .map((optionEl, index) => (
                      <div
                        key={index}
                        className="p-2 mb-2 cursor-pointer dark:hover:bg-neutral-700"
                        onClick={() => {
                          handleChange("occurrence", optionEl.id);
                          updateStartDate(optionEl.vehicleChassis);
                          setOccurrenceOptions(false);
                        }}
                      >
                        <strong>{intl.formatMessage({ id: "intl-msg:calendar.card.chassis" })}</strong> {optionEl.vehicleChassis ?? "-"}{" "}
                        <strong>{intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.modelLabel" })}</strong>{" "}
                        {optionEl.vehicleModel || optionEl.vehicleName || "-"}{" "}
                        <strong>{intl.formatMessage({ id: "intl-msg:appointment.step4.plateLabel" })}</strong>{" "}
                        {optionEl.vehicleLicensePlate ?? "-"}
                      </div>
                    ))}
                  {occurrenceLoading && (
                    <div className="p-2 mb-2 cursor-block">{intl.formatMessage({ id: "intl-msg:appointment.step1.loading" })}</div>
                  )}
                  {!occurrenceLoading && occurrenceHasMoreData && (
                    <div
                      className="p-2 mb-2 cursor-block"
                      onClick={() => getOccurrences(occurrencePage + 1)}
                    >
                      {intl.formatMessage({ id: "intl-msg:monitoring.loadMore" })}
                    </div>
                  )}
                  {!occurrenceLoading &&
                    !occurrenceHasMoreData &&
                    occurrences.length === 0 && (
                      <div className="p-2 mb-2 cursor-block">
                        {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.noResults" })}
                      </div>
                    )}
                </div>
              )}

              <div className="pt-5">
                <label className="block text-size-16 mb-1 font-semibold">
                  {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.box" })}
                </label>
                {isView ? (
                  <div className="text-[14px] text-[#98A2B3] mt-2">
                    {initialData?.serviceBay?.name || ""}
                  </div>
                ) : (
                  <Select
                    className="form-group-lg icon-right"
                    options={serviceBays.map((serviceBay) => ({
                      id: serviceBay.id!,
                      label: serviceBay.name,
                      value: serviceBay.id,
                    }))}
                    value={form.serviceBay ? [form.serviceBay] : undefined}
                    onChange={(item) =>
                      handleChange("serviceBay", item?.id || "")
                    }
                    disabled={isView}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-6 mt-[15px]">
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.startDate" })}
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] mt-2">
                      {initialData?.startDate?.toLocaleDateString(intl.locale, { day: "2-digit", month: "2-digit", year: "numeric" }) || ""}
                    </div>
                  ) : (
                    <DatePicker
                      className="form-group-lg icon-right"
                      value={form.startDate || ""}
                      onChange={(val) => handleChange("startDate", typeof val === "string" ? new Date(val) : val.toDate())}
                      inputProps={{ placeholder: intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.chooseDate" }) }}
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      locale={intl.locale}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.startTime" })}
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] mt-2">
                      {initialData?.startDate?.toLocaleTimeString(intl.locale, { hour: "2-digit", minute: "2-digit" }) || ""}
                    </div>
                  ) : (
                    <TimePicker
                      inputProps={{ className: "timePicker" }}
                      className="form-group-lg icon-right"
                      value={form.startTime?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) || ""}
                      onChange={(val) => {
                        const [hours, minutes] = val.split(":");
                        handleChange("startTime", new Date((form.startDate ?? new Date()).setHours(parseInt(hours), parseInt(minutes), 0, 0)));
                      }}
                      showIcon
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-[15px]">
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.endDate" })}
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] mt-2">
                      {initialData?.endDate?.toLocaleDateString(intl.locale, {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })}
                    </div>
                  ) : (
                    <DatePicker
                      className="form-group-lg icon-right"
                      value={form.endDate || ""}
                      onChange={(val) => handleChange("endDate", typeof val === "string" ? new Date(val) : val.toDate())}
                      inputProps={{ placeholder: intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.chooseDate" }) }}
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      locale={intl.locale}
                    />
                  )}
                </div>
                <div>
                  <label className="block text-size-16 mb-1 font-semibold">
                    {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.endTime" })}
                  </label>
                  {isView ? (
                    <div className="text-[14px] text-[#98A2B3] mt-2">
                      {initialData?.endDate?.toLocaleTimeString(intl.locale, {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  ) : (
                    <TimePicker
                      inputProps={{ className: "timePicker border-1" }}
                      className="form-group-lg icon-right"
                      value={endTimePickerValue}
                      onChange={(val) => {
                        const [hours, minutes] = val.split(":");
                        handleChange("endTime", new Date((form.endDate ?? new Date()).setHours(parseInt(hours), parseInt(minutes), 0, 0)));
                        setEndTimePickerValue(val);
                      }}
                      showIcon
                    />
                  )}
                </div>
              </div>

              {!isView && form.serviceBay &&
                (!isServiceBayAvailable ? (
                  <div className="flex justify-end mt-3">
                    <div className="text-[14px] text-[#DC2626] dark:text-red-400 bg-[#FEF2F2] dark:bg-red-950 border border-[#FCA5A5] dark:border-red-900 px-3 py-1 inline-block rounded-full mt-3">
                      {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.unavailable" })}
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end mt-3">
                    <div className="text-[14px] text-[#98A2B3] bg-[#ECFDF3] dark:bg-green-950 border border-[#ABEFC6] dark:border-green-900 px-3 py-1 inline-block rounded-full mt-3">
                      {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.available" })}
                    </div>
                  </div>
                ))}
            </div>
          </Dialog.Body>

          <Dialog.Footer>
            <div className="flex justify-between w-full py-[5px]">
              <button
                type="button"
                className={`btn-link text-[14px] text-[#98A2B3] mt-2 ${isDeleting ? "btn btn-loading disabled" : ""
                  } ${initialData?.id ? "btn btn-danger" : ""}`}
                onClick={() => setIsDeleting(true)}
                disabled={isDeleting || !initialData?.id}
              >
                {intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.delete" })}
              </button>
              {!isView ? (
                <Button
                  className={`btn btn-primary ${isLoading ? "btn-loading disabled" : ""
                    }`}
                  type="submit"
                  disabled={
                    isLoading ||
                    !!scheduleServiceBays.find(
                      (schedule) => schedule.occurrence?.id === form.occurrence
                    ) || !isServiceBayAvailable
                  }
                >
                  {intl.formatMessage({ id: "intl-msg:occurrence.save" })}
                </Button>
              ) : (
                <div />
              )}
            </div>
          </Dialog.Footer>
        </form>
      </Dialog>

      <ConfirmDialog
        show={isDeleting}
        title={intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.deleteTitle" })}
        body={intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.deleteBody" })}
        onClose={() => setIsDeleting(false)}
        onConfirm={async () => {
          setIsDeleting(true);
          try {
            await onDelete(initialData?.id);
            onClose();
          } catch (error: any) {
            setError(error.message || intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.deleteError" }));
          } finally {
            setIsDeleting(false);
          }
        }}
      />

      <ErrorDialog
        show={!!error}
        title={intl.formatMessage({ id: "intl-msg:dealership.serviceBayScheduleDialog.errorTitle" })}
        body={error}
        onClose={() => setError(null)}
      />
    </>
  );
};

export default ServiceBayScheduleDialog;
